import '../common.css';

import { Stack } from '@chakra-ui/react';
import HCaptcha from '@hcaptcha/react-hcaptcha';
import cx from 'classnames';
import React, { ChangeEvent, Component, Ref } from 'react';
import { connect } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { AppConfig as appConfig } from 'shared/app.config';
import AppConfig from 'shared/config';
import * as shortId from 'shortid';
import { dialCodes } from 'store/countryCodes';
import {
  FocusBlock,
  Grid,
  Heading,
  Para,
  PulseLoader,
  SelectBox,
  SpinLoader,
  Theme,
  Tooltip,
} from 'uie/components';

import { onLoginSuccess } from '../../../core/actions/auth';
import { T_WA_GS_EMAIL_ENTERED, T_WA_GS_REGISTER_PAGE_VIEWED } from '../../../core/const/tracker';
import { exception } from '../../../core/exception';
import { emailAndDomainValidation, isEmailValid } from '../../../core/helpers';
import { IAppState } from '../../../core/interfaces/IAppState';
import { IComponentErrorState, IComponentState } from '../../../core/interfaces/IComponentState';
import { AuthService } from '../../../core/services';
import { AppTracker } from '../../../shared/analytics/tracker';
import { CommonAuth } from '../commonAuth';

const { theme } = Theme;

interface IProps extends Pick<IAppState, 'auth'> {
  onLoginSuccess: (sessionId: string) => void;
}

interface IState {
  componentState: IComponentState;
  onboardState: 'email' | 'password' | 'onboard';
  email: string;
  password: string;
  captcha: string;
  registerAuthToken: string;
  firstName: string;
  lastName: string;
  orgName: string;
  dialCode: string;
  countryCodeSearch: string;
  phoneNumber: string;
  showPassword: boolean;
  errors: IComponentErrorState;
  registeredTrial: boolean;
  onboarded: boolean;
  redirectUrl: string;
  time: string;
}

class GetStarted extends Component<IProps, IState> {
  private _authService = new AuthService();
  private _captcha: HCaptcha | null = null;

  constructor(props: IProps) {
    super(props);

    this.state = {
      componentState: 'idle',
      onboardState: 'email',
      email: '',
      password: '',
      captcha: '',
      registerAuthToken: '',
      firstName: '',
      lastName: '',
      orgName: '',
      showPassword: false,
      dialCode: '+91',
      phoneNumber: '',
      countryCodeSearch: '',
      errors: {},
      registeredTrial: false,
      onboarded: false,
      redirectUrl: '',
      time: '',
    };
  }

  componentDidMount() {
    const { email, redirectFrom } = CommonAuth.db;
    if (redirectFrom === 'a0' && email.length > 0) {
      this.setState({ email, onboardState: 'password' });
    }
    CommonAuth.clearDb();
    AppTracker.track(T_WA_GS_REGISTER_PAGE_VIEWED);

    window.addEventListener('online', this.onOnline);
    window.addEventListener('offline', this.onOffline);

    setTimeout(() => this.loadRecaptchaJS(), 1000);
  }

  loadRecaptchaJS() {
    if (appConfig.recaptcha_site_key) {
      const script = document.createElement('script');
      script.src = 'https://www.google.com/recaptcha/api.js?render=' + appConfig.recaptcha_site_key;
      script.async = true;

      document.body.appendChild(script);
    } else {
      console.log('Recaptcha not loaded, invalid key', appConfig.recaptcha_site_key);
    }
  }

  captcha = async () => {
    const token = await (window as any).grecaptcha.execute(appConfig.recaptcha_site_key, {
      action: 'submit',
    });

    if (token) {
      this.setState({ captcha: token });
    }
  };

  componentWillUnmount() {
    window.removeEventListener('online', this.onOnline);
    window.removeEventListener('offline', this.onOffline);
  }

  onOnline = () => {
    this.setState(state => ({
      ...state,
      errors: {
        ...state.errors,
        network: undefined,
      } as any,
    }));
  };

  onOffline = () => {
    this.setState(state => ({
      ...state,
      errors: {
        ...state.errors,
        network: 'No Network connection, please connect to the internet.',
      } as any,
    }));
  };

  public togglePasswordVisible = () => {
    this.setState(({ showPassword }) => ({ showPassword: !showPassword }));
  };

  onValueChange =
    (key: 'email' | 'password' | 'firstName' | 'lastName' | 'orgName' | 'phoneNumber') =>
    (e: ChangeEvent<HTMLInputElement>) => {
      const newState: any = {};
      newState[key] = e.target.value || '';
      newState.errors = {};
      this.setState(newState);
    };

  validateEmail = async () => {
    try {
      const response = await this._authService.validateEmail(this.state.email);

      if (response.status !== 200 && response.status !== 204) {
        this.setState({
          errors: {
            email: response.data.meta.error_message,
          },
        });
        return false;
      }

      // Only in Prod Env
      const env = AppConfig.VITE_ENV || 'stage';

      if (env !== 'prod' && env !== 'eu-prod') {
        return true;
      }

      const euResponse = await this._authService.validateEmail(this.state.email, { envEu: true });

      if (euResponse.status !== 200 && euResponse.status !== 204) {
        this.setState({
          errors: {
            email: euResponse.data.meta.error_message,
          },
        });
        return false;
      }

      return true;
    } catch (err) {
      console.error(err);
      return false;
    }
  };

  registerTrial = async () => {
    try {
      const response = await this._authService.registerTrial({
        email: this.state.email,
        password: this.state.password,
        token: this.state.captcha,
      });

      if (response.status === 200 || response.status === 204) {
        if (response.data.data.used_personal_email == true) {
          this.setState({
            errors: {
              email: 'This email is already in use',
            },
          });
          return false;
        } else {
          localStorage.setItem('email', this.state.email);
          this.setState({ registerAuthToken: response.data.data.token });
          return true;
        }
      }

      this.setState({
        errors: {
          network: response.data.meta.error_message,
        },
      });
      return false;
    } catch (err) {
      this.setState({
        errors: {
          network: 'Something went wrong, please try again',
        },
      });
      console.error(err);
      return false;
    }
  };

  onboard = async () => {
    try {
      const response = await this._authService.onboard({
        first_name: this.state.firstName,
        last_name: this.state.lastName,
        organization: this.state.orgName,
        contact:
          this.state.phoneNumber?.length > 0 && this.state.dialCode?.length > 0
            ? {
                dial_code: this.state.dialCode,
                phone_number: this.state.phoneNumber,
              }
            : {},
        token: this.state.registerAuthToken,
      });

      if (response.status === 200 || response.status === 204) {
        if (response.data.data.redirect_url?.length) {
          window.location.href = response.data.data.redirect_url;
          this.setState({
            redirectUrl: response.data.data.redirect_url,
          });
          return true;
        }
      }

      this.setState({
        errors: {
          network: response.data.error_message,
        },
      });
      return false;
    } catch (error) {
      this.setState({
        errors: {
          network: 'Something went wrong, please try again',
        },
      });
      console.error(error);
      return false;
    }
  };

  onVerify = () => {
    const { email, password, captcha, firstName, orgName, onboardState } = this.state;
    const errors: any = {};

    if (onboardState === 'email') {
      const validationResult = emailAndDomainValidation(email);

      if (validationResult.error) {
        errors.email = validationResult.message;
      }
    }

    if (onboardState === 'password' && passwordVerification(password)) {
      errors.password = 'Password should be between 10 and 72 characters';
    }

    if (onboardState === 'password' && captcha === '') {
      errors.captcha = 'Please solve the captcha before submitting.';
    }

    if (onboardState === 'onboard') {
      if (firstName === '') {
        errors.firstName = 'First name is required';
      }

      if (orgName === '') {
        errors.orgName = 'Organization name is required.';
      }
    }

    if (!navigator.onLine) {
      errors.network = 'No Network connection, please connect to the internet.';
    }

    this.setState(state => ({
      ...state,
      errors: {
        ...state.errors,
        ...errors,
      } as any,
    }));
    return Object.entries(errors).length === 0;
  };

  onSubmit = async (e?: any) => {
    if (e) {
      e.preventDefault();
    }

    if (!this.onVerify()) {
      return;
    }

    this.setState({ componentState: 'busy' });

    const { onboardState } = this.state;

    try {
      if (onboardState !== 'onboard') {
        const emailIsValid = await this.validateEmail();

        if (!emailIsValid) {
          return;
        }
      }

      if (onboardState === 'email') {
        this.setState({ onboardState: 'password' });
        AppTracker.track(T_WA_GS_EMAIL_ENTERED, { Email: this.state.email });
        return;
      }

      if (onboardState === 'password') {
        const success = await this.registerTrial();

        this.clearCaptcha();

        if (success) {
          const time = new Date().getTime().toString();
          this.setState({ onboardState: 'onboard', registeredTrial: true, time: time });
          AppTracker.trackSignUp({ email: this.state.email });
          return;
        }
      }

      if (onboardState === 'onboard') {
        const success = await this.onboard();

        if (success) {
          this.clearCaptcha();
          this.setState({ onboarded: true });
          return;
        }
      }
    } catch (err) {
      this.setState({
        errors: {
          network: 'Something went wrong. Please refresh the page and try again',
        },
      });
      exception.handle('E_START_EMAIL_CHECK_FAILED', err);
    } finally {
      this.setState({ componentState: 'idle' });
    }
  };

  onCaptchaVerify = (token: string) => {
    this.setState({ captcha: token });
  };

  clearCaptcha = () => {
    this.setState({ captcha: '' }, () => {
      this._captcha?.resetCaptcha();
    });
  };

  render() {
    const {
      componentState,
      email,
      errors,
      password,
      captcha,
      firstName,
      lastName,
      orgName,
      phoneNumber,
      onboardState,
      showPassword,
      registeredTrial,
      onboarded,
      redirectUrl,
    } = this.state;

    const selectedDial = dialCodes.find(
      ({ dial_code }: any) => dial_code === this.state.dialCode,
    ) || {
      dial_code: '',
      code: '',
    };

    if (onboarded) {
      return <Redirect to={redirectUrl || '/'} exact={true} />;
    }

    return (
      <div className="login-container">
        <div className="login-interact">
          <div className="mb-10">
            <Heading fontSize={24} height={24}>
              Incident response for
            </Heading>
            <br />
            <Heading fontSize={24} height={24}>
              SRE, DevOps & IT Teams
            </Heading>
          </div>
          <Heading className="mt-10" fontSize={16} fontWeight={300} height={16}>
            Get Started For Free.
          </Heading>

          <form onSubmit={this.onSubmit} style={{ marginTop: 40 }}>
            {registeredTrial ? (
              <div>
                {/** // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              @ts-ignore */}
                <Stack gap={32}>
                  <div className="login-input-container">
                    <label className="login-label" htmlFor="onboard_first_name">
                      First Name
                    </label>
                    <input
                      id="onboard_first_name"
                      className={cx('login-input', {
                        error: errors.firstName && errors.firstName.length > 0,
                      })}
                      value={firstName}
                      onChange={this.onValueChange('firstName')}
                      placeholder="First Name"
                      type="text"
                      disabled={componentState === 'busy'}
                    />
                  </div>

                  <div className="login-input-container">
                    <label className="login-label" htmlFor="onboard_last_name">
                      Last Name
                    </label>
                    <input
                      id="onboard_last_name"
                      className={cx('login-input', {
                        error: errors.lastName && errors.lastName.length > 0,
                      })}
                      value={lastName}
                      onChange={this.onValueChange('lastName')}
                      placeholder="Last Name"
                      type="text"
                      disabled={componentState === 'busy'}
                    />
                  </div>

                  <div className="login-input-container">
                    <label className="login-label" htmlFor="onboard_organization_name">
                      Organization
                    </label>
                    <input
                      id="onboard_organization_name"
                      className={cx('login-input', {
                        error: errors.firstName && errors.firstName.length > 0,
                      })}
                      value={orgName}
                      onChange={this.onValueChange('orgName')}
                      placeholder="Organization"
                      type="text"
                      disabled={componentState === 'busy'}
                    />
                  </div>

                  <div className="login-input-container">
                    <label className="login-label" htmlFor="onboard_phone_number">
                      Phone Number
                    </label>
                    <Grid>
                      <div
                        style={{ width: 130, marginRight: 10, position: 'relative', top: '1px' }}
                      >
                        <SelectBox
                          hook={
                            <div>
                              <span>{selectedDial.dial_code}</span>
                              <span style={{ color: theme.shades.grey, marginLeft: 10 }}>
                                {selectedDial.code}
                              </span>
                            </div>
                          }
                          onValueChange={(_, v: string) => {
                            this.setState({ dialCode: v });
                          }}
                          searchHookProps={{
                            value: this.state.countryCodeSearch,
                            onChange: e => this.setState({ countryCodeSearch: e.target.value }),
                          }}
                          height="auto"
                          width="100%"
                          maxWidth="100%"
                        >
                          {dialCodes
                            .filter(({ name, dial_code }: { name: string; dial_code: string }) => {
                              return (
                                this.state.countryCodeSearch === '' ||
                                name.toLowerCase().indexOf(this.state.countryCodeSearch) > -1 ||
                                dial_code.toLowerCase().indexOf(this.state.countryCodeSearch) > -1
                              );
                            })
                            .map(({ dial_code, code }: { dial_code: string; code: string }) => (
                              <FocusBlock
                                value={dial_code}
                                key={shortId.generate()}
                                isSelected={dial_code === this.state.dialCode}
                              >
                                <div>
                                  <span>{dial_code}</span>
                                  <span style={{ color: theme.shades.grey, marginLeft: 10 }}>
                                    {code}
                                  </span>
                                </div>
                              </FocusBlock>
                            ))}
                        </SelectBox>
                      </div>
                      <input
                        id="onboard_phone_number"
                        className={cx('login-input', {
                          error: errors.firstName && errors.firstName.length > 0,
                        })}
                        value={phoneNumber}
                        onChange={this.onValueChange('phoneNumber')}
                        placeholder="Phone Number"
                        type="text"
                        disabled={componentState === 'busy'}
                      />
                    </Grid>
                  </div>
                </Stack>
              </div>
            ) : (
              <div>
                <div className="login-input-container">
                  <label className="login-label" htmlFor="login_email">
                    Work Email
                  </label>
                  <Grid justifyContent="flex-start" alignItems="center">
                    <input
                      id="login_email"
                      className={cx('login-input', {
                        error: errors.email && errors.email.length > 0,
                      })}
                      value={email}
                      onChange={this.onValueChange('email')}
                      placeholder="example@company.com"
                      type="email"
                      disabled={onboardState === 'password' || componentState === 'busy'}
                    />
                    <div className="ml-10">
                      {componentState === 'busy' && onboardState === 'email' && (
                        <SpinLoader base="12px" />
                      )}
                      {onboardState === 'password' && (
                        <img src="/icons/check_green.svg" alt="good" />
                      )}
                    </div>
                  </Grid>
                  <p className="login-error-message">
                    {errors.email || ''}
                    {errors.email && errors.email.includes('invalid') && (
                      <Tooltip
                        label="A valid email address would give us a way to initiate account verification"
                        offset={{
                          top: '30px',
                          left: '-60px',
                        }}
                      >
                        <Para fontSize={12} color={theme.danger.default}>
                          <u className="ml-5">know more</u>
                        </Para>
                      </Tooltip>
                    )}
                  </p>
                </div>

                {onboardState === 'password' && (
                  <div className="login-input-container">
                    <label className="login-label" htmlFor="login_password">
                      Set password
                    </label>
                    <Grid justifyContent="flex-start" alignItems="center">
                      <input
                        id="login_password"
                        className={cx('login-input', {
                          error: errors.password && errors.password.length > 0,
                        })}
                        value={password}
                        onChange={this.onValueChange('password')}
                        placeholder="10+ Characters"
                        type={showPassword ? 'text' : 'password'}
                        disabled={componentState === 'busy'}
                        onFocus={this.captcha}
                      />
                      {password.length > 0 && (
                        <img
                          style={{ marginLeft: -40, cursor: 'pointer' }}
                          src={`/icons/${!showPassword ? 'show_password' : 'hide_password'}.svg`}
                          alt="show password"
                          onClick={this.togglePasswordVisible}
                        />
                      )}
                    </Grid>
                    {errors.password && (
                      <p className="login-error-message">{errors.password || ''}</p>
                    )}
                  </div>
                )}
              </div>
            )}

            <p className="login-error-message">{errors.network || ''}</p>

            <button
              className="login-get-started"
              style={{ marginTop: 0 }}
              disabled={
                (onboardState === 'email' && email === '') ||
                (onboardState === 'password' && password === '') ||
                (onboardState === 'password' && captcha === '') ||
                (onboardState === 'onboard' && firstName === '') ||
                (onboardState === 'onboard' && orgName === '') ||
                componentState === 'busy'
              }
              onClick={this.onSubmit}
            >
              {componentState === 'busy' && onboardState === 'password' ? (
                <Grid alignContent="center" justifyContent="center">
                  <PulseLoader color={theme.shades.white} base="12px" />
                </Grid>
              ) : onboardState === 'email' || onboardState === 'password' ? (
                'Get Started'
              ) : (
                'Start Your Free Trial'
              )}
            </button>
          </form>

          <div className="login-account-exists">
            <span>Already have an account?</span>
            <Link className="login-signin-link" to="/">
              Login
            </Link>
          </div>

          <div className="login-t-c">
            <span>
              By signing up I am agreeing with the and
              <a
                className="login-link"
                target="_blank"
                href="https://www.squadcast.com/terms"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              <span style={{ marginLeft: 10 }}>and</span>
              <a
                className="login-link"
                target="_blank"
                href="https://www.squadcast.com/privacy"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>
            </span>
          </div>
        </div>
      </div>
    );
  }
}

function emailVerification(email: string): string {
  if (isEmailValid(email)) {
    return '';
  }

  return "Doesn't Look right. Try Again ?";
}

function passwordVerification(password: string): string {
  if (password.length >= 10 && password.length <= 128) {
    return '';
  }
  return 'Password should be between 10 and 128 characters';
}

export default connect(({ auth }: IAppState) => ({ auth }), {
  onLoginSuccess,
})(GetStarted);
